import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import serviceApi from '../../services/serviceApi';
import { PAYMENT_METHOD_LABELS } from '../../config/serviceConstants';
import { formatSAR } from '../../utils/currencyFormatter';
import { arabicErrorFor } from '../../utils/serviceErrorMessages';

const PAYABLE_METHODS = ['bank_transfer', 'cash'];

// `defaultPaidAt` is the server's business date for this operation (Riyadh).
// It must NOT be computed here: toISOString() returns the UTC day, which pre-fills
// yesterday between 00:00 and 03:00 Riyadh — on the 1st, into the previous month.
// Empty string when absent, so the field stays blank rather than silently wrong.
const RecordPaymentModal = ({ show, onHide, invoice, defaultPaidAt, onRecorded }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);

  const total = invoice?.commercials?.totalAmount || 0;

  const [form, setForm] = useState({
    amount: '',
    method: 'bank_transfer',
    paidAt: '',
    note: ''
  });

  // Re-seeded every time the modal opens — a default computed once at mount goes
  // stale the moment the page outlives the business day.
  useEffect(() => {
    if (show) {
      setForm({
        amount: total ? total.toFixed(2) : '',
        method: 'bank_transfer',
        paidAt: defaultPaidAt || '',
        note: ''
      });
      setError(null);
    }
  }, [show, total, defaultPaidAt]);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    setError(null);
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) {
      setError('يرجى إدخال مبلغ صحيح');
      return;
    }
    setSaving(true);
    try {
      await serviceApi.invoices.recordPayment(invoice._id, {
        amount: amt,
        method: form.method,
        paidAt: form.paidAt,
        note: form.note || undefined
      });
      onRecorded?.();
      onHide();
    } catch (err) {
      setError(arabicErrorFor(err, 'تعذر تسجيل الدفعة'));
    } finally {
      setSaving(false);
    }
  };

  if (!invoice) return null;

  return (
    <Modal show={show} onHide={onHide} centered dir="rtl">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '1.1rem', fontWeight: 700, color: '#471396' }}>
          تسجيل دفعة
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" className="py-2">{error}</Alert>}

        <div style={{ fontSize: '0.85rem', color: '#6c757d', marginBottom: 16 }}>
          الفاتورة: <strong>{invoice.invoiceNumber}</strong>
          {' • '}
          المستحق: <strong>{formatSAR(total)}</strong>
        </div>

        <div className="d-flex gap-3 mb-3">
          <Form.Group style={{ flex: 1 }}>
            <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>المبلغ (ر.س) *</Form.Label>
            <Form.Control
              className="touch-input"
              type="number"
              min="0"
              step="0.01"
              value={form.amount}
              onChange={(e) => handleChange('amount', e.target.value)}
              dir="ltr"
            />
          </Form.Group>
          <Form.Group style={{ flex: 1 }}>
            <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>طريقة الدفع *</Form.Label>
            <Form.Select
              className="touch-input"
              value={form.method}
              onChange={(e) => handleChange('method', e.target.value)}
            >
              {PAYABLE_METHODS.map(m => (
                <option key={m} value={m}>{PAYMENT_METHOD_LABELS[m] || m}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </div>

        <Form.Group className="mb-3">
          <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>تاريخ الدفع *</Form.Label>
          <Form.Control
            className="touch-input"
            type="date"
            value={form.paidAt}
            onChange={(e) => handleChange('paidAt', e.target.value)}
            dir="ltr"
          />
        </Form.Group>

        <Form.Group className="mb-2">
          <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>ملاحظة</Form.Label>
          <Form.Control
            className="touch-input"
            as="textarea"
            rows={2}
            value={form.note}
            onChange={(e) => handleChange('note', e.target.value)}
            placeholder="اختياري..."
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={saving}>إلغاء</Button>
        <Button
          style={{ background: '#471396', borderColor: '#471396' }}
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? <span className="spinner-border spinner-border-sm" /> : 'تسجيل الدفعة'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RecordPaymentModal;
